<template>
    <div class="h-100">
        <b-container class="p-0" fluid>
            <b-row align-h="center">
                <b-col cols="12" md="10">
                    <b-link :to="{ name: 'contacts' }">
                        <font-awesome-icon icon="arrow-left" />
                        {{ $t("Back") }}
                    </b-link>
                    <h1 class="mt-1">{{ $t("mainmenu.contacts") }} / {{ $t("Edit") }}</h1>

                    <hr />

                    <div v-if="isLoading" class="d-flex justify-content-center mb-3">
                        <b-spinner />
                    </div>

                    <validation-observer v-show="!isLoading" ref="validationRules" tag="form">
                        <b-form class="mt-1">
                            <b-row>
                                <b-col lg="6" order="2" order-lg="1" xs="auto">
                                    <b-form-group
                                        :label="$t('entities.contact.Important contact')"
                                        content-cols-lg="1"
                                        content-cols-sm
                                        label-cols-lg="6"
                                        label-cols-sm="4"
                                        label-for="isHighPriority"
                                    >
                                        <b-form-checkbox
                                            v-model="contact.isHighPriority"
                                            :checked="contact.isHighPriority"
                                            class="pl-5p"
                                            inline
                                            name="isHighPriority"
                                            switch
                                            unchecked-value="0"
                                            value="1"
                                        >
                                        </b-form-checkbox>
                                    </b-form-group>

                                    <b-form-group
                                        :label="$t('entities.contact.Block')"
                                        content-cols-lg="1"
                                        content-cols-sm
                                        label-cols-lg="6"
                                        label-cols-sm="4"
                                        label-for="isBlocked"
                                    >
                                        <b-form-checkbox
                                            v-model="contact.isBlocked"
                                            :checked="contact.isBlocked"
                                            class="pl-5p"
                                            inline
                                            name="isBlocked"
                                            switch
                                            unchecked-value="0"
                                            value="1"
                                        >
                                        </b-form-checkbox>
                                    </b-form-group>

                                    <b-form-group
                                        :label="$t('entities.contact.allow-campaign-messages')"
                                        content-cols-lg="1"
                                        content-cols-sm
                                        label-cols-lg="6"
                                        label-cols-sm="4"
                                        label-for="allowSystemMessages"
                                    >
                                        <b-form-checkbox
                                            v-model="contact.allowSystemMessages"
                                            :checked="contact.allowSystemMessages"
                                            class="pl-5p"
                                            inline
                                            name="allowSystemMessages"
                                            switch
                                            unchecked-value="0"
                                            value="1"
                                        >
                                        </b-form-checkbox>
                                    </b-form-group>
                                </b-col>
                                <b-col class="pr-11p" lg="4" order="1" order-lg="2" xs="auto">
                                    <contact-labels v-if="contact" id="container-contact-labels" :contact="contact" />
                                </b-col>
                            </b-row>

                            <b-form-group
                                :label="`${$t('entities.contact.Name')}*`"
                                content-cols-lg="7"
                                content-cols-sm
                                label-cols-lg="3"
                                label-cols-sm="4"
                                label-for="name"
                                name="name"
                            >
                                <validation-provider #default="{ errors }" name="name" rules="required">
                                    <b-form-input
                                        id="name"
                                        v-model="contact.name"
                                        :state="errors.length > 0 ? false : null"
                                        type="text"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>

                            <b-form-group
                                content-cols-lg="7"
                                content-cols-sm
                                label-cols-lg="3"
                                label-cols-sm="4"
                                label-for="phone"
                                name="phone"
                            >
                                <template v-slot:label>
                                    {{ $t("entities.contact.Phone") }}
                                    <font-awesome-icon
                                        v-b-tooltip.hover.right="
                                            $t(
                                                original && original.phone
                                                    ? 'contacts.phone-not-editable'
                                                    : 'phone-must-be-unique'
                                            )
                                        "
                                        class="ml-1"
                                        icon="square-info"
                                    />
                                </template>
                                <validation-provider #default="{ errors }" name="phone" rules="libphonenumber">
                                    <b-form-input
                                        id="phone"
                                        v-model="contact.phone"
                                        :disabled="original ? Boolean(original.phone) : false"
                                        @blur="contact.phone = parsePhoneNumber(contact.phone)"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>

                            <b-form-group
                                content-cols-lg="7"
                                content-cols-sm
                                label-cols-lg="3"
                                label-cols-sm="4"
                                label-for="email"
                                name="email"
                            >
                                <template v-slot:label>
                                    {{ $t("entities.contact.Email") }}
                                    <font-awesome-icon
                                        v-b-tooltip.hover.right="
                                            $t(
                                                original && original.email
                                                    ? 'contacts.email-not-editable'
                                                    : 'field-must-be-unique'
                                            )
                                        "
                                        class="ml-1"
                                        icon="square-info"
                                    />
                                </template>
                                <validation-provider #default="{ errors }" name="email" rules="email">
                                    <b-form-input
                                        id="email"
                                        v-model="contact.email"
                                        :disabled="original ? Boolean(original.email) : false"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>

                            <b-form-group
                                :label="$t('entities.contact.Street')"
                                content-cols-lg="7"
                                content-cols-sm
                                label-cols-lg="3"
                                label-cols-sm="4"
                                label-for="street"
                                name="Street"
                            >
                                <b-form-input id="street" v-model="contact.street" />
                            </b-form-group>

                            <b-form-group
                                :label="$t('entities.contact.House number')"
                                content-cols-lg="7"
                                content-cols-sm
                                label-cols-lg="3"
                                label-cols-sm="4"
                                label-for="houseNumber"
                                name="House number"
                            >
                                <b-form-input id="houseNumber" v-model="contact.houseNumber" />
                            </b-form-group>

                            <b-form-group
                                :label="$t('entities.contact.Zip code')"
                                content-cols-lg="7"
                                content-cols-sm
                                label-cols-lg="3"
                                label-cols-sm="4"
                                label-for="zipCode"
                                name="Zip code"
                            >
                                <b-form-input id="zipCode" v-model="contact.zipCode" />
                            </b-form-group>

                            <b-form-group
                                :label="$t('entities.contact.City')"
                                content-cols-lg="7"
                                content-cols-sm
                                label-cols-lg="3"
                                label-cols-sm="4"
                                label-for="city"
                                name="City"
                            >
                                <b-form-input id="city" v-model="contact.city" />
                            </b-form-group>

                            <b-form-group
                                :label="$t('entities.contact.State')"
                                content-cols-lg="7"
                                content-cols-sm
                                label-cols-lg="3"
                                label-cols-sm="4"
                                label-for="state"
                                name="State"
                            >
                                <b-form-input id="state" v-model="contact.province" />
                            </b-form-group>

                            <b-form-group
                                :label="$t('entities.contact.Company')"
                                content-cols-lg="7"
                                content-cols-sm
                                label-cols-lg="3"
                                label-cols-sm="4"
                                label-for="company"
                                name="Company"
                            >
                                <b-form-input id="company" v-model="contact.company" />
                            </b-form-group>

                            <b-form-group
                                :label="$t('entities.contact.Job title')"
                                content-cols-lg="7"
                                content-cols-sm
                                label-cols-lg="3"
                                label-cols-sm="4"
                                label-for="jobTitle"
                                name="jobTitle"
                            >
                                <b-form-input id="jobTitle" v-model="contact.jobTitle" />
                            </b-form-group>

                            <b-form-group
                                :label="$t('entities.contact.Country')"
                                content-cols-lg="7"
                                content-cols-sm
                                label-cols-lg="3"
                                label-cols-sm="4"
                                label-for="country"
                                name="country"
                            >
                                <b-form-select
                                    id="country"
                                    v-model="contact.countryCode"
                                    :options="getCountriesAsArray()"
                                    text-field="name"
                                    value-field="code"
                                />
                            </b-form-group>

                            <b-form-group
                                :label="$t('entities.contact.Reference')"
                                content-cols-lg="7"
                                content-cols-sm
                                label-cols-lg="3"
                                label-cols-sm="4"
                                label-for="reference"
                                name="Reference"
                            >
                                <b-form-input id="reference" v-model="contact.reference" />
                            </b-form-group>

                            <b-form-group
                                v-for="metadata in contact.metadata"
                                :key="metadata.name"
                                :label="metadata.name"
                                :label-for="metadata.name"
                                :name="metadata.name"
                                content-cols-lg="7"
                                content-cols-sm
                                label-cols-lg="3"
                                label-cols-sm="4"
                            >
                                <b-form-input :id="metadata.name" v-model="metadata.value" />
                            </b-form-group>

                            <hr />

                            <b-row class="mt-2">
                                <b-col offset-md="3">
                                    <b-button
                                        id="submit"
                                        :block="$store.getters['app/currentBreakPoint'] === 'xs'"
                                        :disabled="isSaving"
                                        class="d-flex justify-content-center mb-2"
                                        variant="primary"
                                        @click="submit"
                                    >
                                        <b-spinner v-if="isSaving" class="mr-50" small></b-spinner>
                                        {{ $t("Save") }}
                                    </b-button>
                                </b-col>
                            </b-row>
                        </b-form>
                    </validation-observer>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script>
import {
    BAlert,
    BButton,
    BCol,
    BContainer,
    BForm,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BLink,
    BRow,
    BSpinner,
    VBTooltip,
} from "bootstrap-vue";
import router from "@/router";
import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import { isValidPhoneNumber, parsePhoneNumberFromString } from "libphonenumber-js";
import { onMounted, ref } from "@vue/composition-api";
import { required } from "@validations";
import { useToast } from "vue-toastification/composition";
import i18n from "@/libs/i18n";
import { getCountriesAsArray } from "@/utils/countries";
import useContacts from "@/views/modules/contacts/useContacts";
import toastificationContent from "@/layouts/components/toastification/toastificationContent";
import ContactLabels from "@/components/contactLabels.vue";

extend("libphonenumber", (phone) => {
    const findCountryCode = ["+", "00"].filter((countryCode) => phone.startsWith(countryCode));
    if (findCountryCode.length === 0) {
        return false;
    }

    return isValidPhoneNumber(phone, "NL");
});

export default {
    components: {
        ContactLabels,
        BAlert,
        BButton,
        BCol,
        BContainer,
        BForm,
        BFormCheckbox,
        BFormGroup,
        BFormInput,
        BFormSelect,
        BLink,
        BRow,
        BSpinner,
        ValidationObserver,
        ValidationProvider,
        VBTooltip,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    data() {
        return {
            required,
        };
    },
    setup() {
        const toast = useToast();

        const { metadataFields, isLoading, isSaving, updateContact, fetchContact, fetchMetadataFields } = useContacts();
        fetchMetadataFields();

        // Default contact data when contact data has not been fetched yet.
        const contact = ref({
            id: null,
            name: "",
            phone: "",
            email: "",
            street: "",
            houseNumber: "",
            zipCode: "",
            city: "",
            province: "",
            company: "",
            jobTitle: "",
            countryCode: "",
            reference: "",
            isHighPriority: 0,
            isBlocked: 0,
            allowSystemMessages: 1,
        });
        const original = ref(); // we need the original data to skip the "contact exists" check when data has not been changed.

        onMounted(() => {
            fetchContact(router.currentRoute.params.id, {
                join: "metadata||id,name,value",
            })
                .then((response) => {
                    original.value = {
                        phone: response.data.phone,
                        email: response.data.email,
                    };

                    // tmp doing it this way.
                    response.data.metadata = metadataFields.value.map((field) => {
                        return response.data.metadata.find((meta) => meta.name === field.name) ?? { name: field.name };
                    });

                    contact.value = response.data;
                })
                .catch(() => toast.error(t("toast.read.error")));
        });

        const parsePhoneNumber = (phone) => {
            if (!phone) {
                return;
            }

            const findCountryCode = ["+", "00"].filter((countryCode) => phone.startsWith(countryCode));
            if (findCountryCode.length === 0) {
                return phone;
            }

            if (isValidPhoneNumber(phone, "NL")) {
                return parsePhoneNumberFromString(phone, "NL").formatInternational();
            }

            return phone;
        };

        return {
            toast,
            contact,
            original,
            metadataFields,
            isLoading,
            isSaving,
            parsePhoneNumberFromString,
            getCountriesAsArray,
            parsePhoneNumber,

            // Methods
            updateContact,
        };
    },
    methods: {
        submit() {
            const normalizeKey = (str) => {
                return str.replace(/[']/g, "").replace(/\s+/g, "-").trim();
            };
            const normalizeFieldName = (str) => {
                return str
                    .toLowerCase()
                    .replace(/-/g, " ")
                    .trim()
                    .replace(/^\w/, (c) => c.toUpperCase());
            };
            this.$refs.validationRules.validate().then((success) => {
                // Filter out empty metadata to avoid empty values in the database.
                this.contact.metadata = this.contact.metadata.filter((field) => field.value || field.id);

                if (success) {
                    this.updateContact(this.contact, this.original)
                        .then(() => {
                            this.$router.push({ name: "contacts" }, () => {
                                this.toast({
                                    component: toastificationContent,
                                    props: {
                                        title: i18n.t("entities.contact.Contact"),
                                        text: i18n.t("toast.update.success", { entity: this.contact.name }),
                                        icon: "check",
                                        variant: "success",
                                    },
                                });
                            });
                        })
                        .catch((error) => {
                            if (error === "contact-already-exists") {
                                this.toast.error(i18n.t("contacts.contact-already-exists"));
                            } else {
                                const errorMessages = error?.response?.data?.message?.split(",") ?? [];
                                if (errorMessages.length > 0) {
                                    const errorKeys = Object.keys(
                                        (i18n.messages || i18n.messages.value)["en"].contacts.lists.wizard.errors
                                    );

                                    const errors = errorMessages.map((errorString) => {
                                        const matchingKey = errorKeys.find((key) =>
                                            normalizeKey(errorString).includes(normalizeKey(key))
                                        );

                                        if (matchingKey) {
                                            const field = normalizeKey(errorString)
                                                .substring(
                                                    0,
                                                    normalizeKey(errorString).indexOf(normalizeKey(matchingKey))
                                                )
                                                .trim();
                                            return i18n.t(`contacts.lists.wizard.errors.${normalizeKey(matchingKey)}`, {
                                                field: i18n.t(`entities.contact.${normalizeFieldName(field)}`),
                                            });
                                        }
                                    });

                                    errors.forEach((error) => {
                                        this.toast.error(error, { timeout: 6000 });
                                    });
                                } else {
                                    this.toast.error(
                                        i18n.t("toast.create.error", { entity: i18n.t("entities.contact.Contact") })
                                    );
                                }
                            }
                        });
                }
            });
        },
    },
};
</script>

<style lang="scss">
@import "~@/assets/scss/modules/contacts/contacts.scss";
</style>
